import axios from "axios";
import {
  airChfarApi,
  type ShopSummaryResponseV2,
  type AirChfarExerciseShopRequest,
} from "@b2bportal/air-chfar-api";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { setFlights } from "../../../shop/actions/actions";

export const chfarFlightsApi = createApi({
  reducerPath: "chfarFlightsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["ChfarFlights"],
  endpoints: (builder) => ({
    getFlights: builder.query<
      ShopSummaryResponseV2,
      AirChfarExerciseShopRequest
    >({
      queryFn: async (request) => {
        try {
          const res = await airChfarApi(axios).apiV1ChfarAirExerciseShopPost(
            request
          );

          if (res.data.AirChfarExerciseShopResponse === "Failure") {
            throw new Error(res.data.Failure);
          }

          const shopSummaryResponse = res.data.value
            .value as ShopSummaryResponseV2; // opaque so we need to cast
          const { fareIdToPriceDifference } = res.data;

          const coverageAdjustedFares: Record<
            string,
            ShopSummaryResponseV2["flights"]["fares"][number]
          > = Object.entries(shopSummaryResponse.flights.fares).reduce(
            (acc, [fareId, fare]) => {
              if (fareIdToPriceDifference[fareId] == null) {
                acc[fareId] = { ...fare };
              } else {
                acc[fareId] = {
                  ...fare,
                  amount: fareIdToPriceDifference[fareId],
                };
              }

              return acc;
            },
            {}
          );

          const coverageAdjustedOutbound =
            shopSummaryResponse.flights.outbound.map((outboundSlice) => ({
              ...outboundSlice,
              fares: outboundSlice.fares.map((fare) => {
                const priceDifference =
                  fareIdToPriceDifference[fare.example.fare];
                return priceDifference == null
                  ? { ...fare }
                  : { ...fare, amount: priceDifference };
              }),
            }));

          const priceAdjustedShopSummary: ShopSummaryResponseV2 = {
            ...shopSummaryResponse,
            flights: {
              ...shopSummaryResponse.flights,
              fares: coverageAdjustedFares,
              outbound: coverageAdjustedOutbound,
            },
          };

          return { data: priceAdjustedShopSummary };
        } catch (error: unknown) {
          if (axios.isAxiosError(error)) {
            return {
              error: {
                status: error.response?.status,
                message: error.message,
              },
            };
          }

          return {
            error: { error: (error as Error).message ?? "Unknown error" },
          };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        dispatch(setFlights(null));
        await queryFulfilled;
      },
      keepUnusedDataFor: 300,
      providesTags: ["ChfarFlights"],
    }),
  }),
});

export const { useGetFlightsQuery } = chfarFlightsApi;
export const chfarFlightApiReducerPath = chfarFlightsApi.reducerPath;
export const chfarFlightsApiReducer = chfarFlightsApi.reducer;
export const chfarFlightsApiMiddleware = chfarFlightsApi.middleware;

export type IChFARFlightsApiState = ReturnType<typeof chfarFlightsApi.reducer>;
